import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Manages chronometer and countdown timer logic with localStorage persistence.
 * Extracted from StudyTimePage to support reuse in HomePage inline timer.
 *
 * @param {'chronometer'|'timer'} mode
 */
export function useTimer(mode = 'chronometer') {
  // ── Chronometer state ──
  const [chronoTime, setChronoTime] = useState(() => {
    const saved = localStorage.getItem('chronoTime');
    return saved ? Number(saved) : 0;
  });
  const [chronoRunning, setChronoRunning] = useState(() => {
    const saved = localStorage.getItem('chronoRunning');
    return saved ? JSON.parse(saved) : false;
  });
  const chronoInterval = useRef(null);

  // ── Countdown timer state ──
  const [timerRemaining, setTimerRemaining] = useState(() => {
    const saved = localStorage.getItem('timerRemaining');
    return saved ? Number(saved) : 0;
  });
  const [timerRunning, setTimerRunning] = useState(() => {
    const saved = localStorage.getItem('timerRunning');
    return saved ? JSON.parse(saved) : false;
  });
  const [timerStartDuration, setTimerStartDuration] = useState(() => {
    const saved = localStorage.getItem('timerStartDuration');
    return saved ? Number(saved) : 0;
  });
  const timerInterval = useRef(null);

  // ── Format helper ──
  const formatTime = useCallback((seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins
      .toString()
      .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // ── Chronometer effect ──
  useEffect(() => {
    if (chronoRunning) {
      chronoInterval.current = setInterval(() => {
        setChronoTime((prev) => {
          const newTime = prev + 1;
          localStorage.setItem('chronoTime', newTime);
          document.title = `${formatTime(newTime)} | Yelken Çalışma`;
          return newTime;
        });
      }, 1000);
    } else {
      clearInterval(chronoInterval.current);
      if (mode === 'chronometer') {
        document.title = 'Yelken | Hedeflerine Yelken Aç';
      }
    }
    localStorage.setItem('chronoRunning', JSON.stringify(chronoRunning));
    return () => {
      clearInterval(chronoInterval.current);
    };
  }, [chronoRunning, formatTime, mode]);

  // ── Timer effect ──
  const onTimerComplete = useRef(null);

  useEffect(() => {
    if (timerRunning) {
      timerInterval.current = setInterval(() => {
        setTimerRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(timerInterval.current);
            setTimerRunning(false);
            localStorage.setItem('timerRunning', JSON.stringify(false));
            localStorage.setItem('timerRemaining', 0);
            document.title = 'Yelken | Hedeflerine Yelken Aç';
            if (onTimerComplete.current) onTimerComplete.current(timerStartDuration);
            return 0;
          }
          const newRemaining = prev - 1;
          localStorage.setItem('timerRemaining', newRemaining);
          document.title = `${formatTime(newRemaining)} | Yelken Çalışma`;
          return newRemaining;
        });
      }, 1000);
    } else {
      clearInterval(timerInterval.current);
    }
    localStorage.setItem('timerRunning', JSON.stringify(timerRunning));
    return () => clearInterval(timerInterval.current);
  }, [timerRunning, timerStartDuration, formatTime]);

  // ── Chronometer controls ──
  const chronoStart = () => setChronoRunning(true);
  const chronoPause = () => setChronoRunning(false);
  const chronoToggle = () => setChronoRunning((prev) => !prev);

  const chronoReset = () => {
    setChronoTime(0);
    setChronoRunning(false);
    localStorage.setItem('chronoTime', 0);
    localStorage.setItem('chronoRunning', JSON.stringify(false));
  };

  const chronoGetAndReset = () => {
    const elapsed = chronoTime;
    chronoReset();
    return elapsed;
  };

  // ── Timer controls ──
  const timerStart = (hours = 0, minutes = 0, seconds = 0) => {
    const totalSeconds = hours * 3600 + minutes * 60 + seconds;
    if (totalSeconds <= 0) return false;

    setTimerStartDuration(totalSeconds);
    setTimerRemaining(totalSeconds);
    setTimerRunning(true);
    localStorage.setItem('timerStartDuration', totalSeconds);
    localStorage.setItem('timerRemaining', totalSeconds);
    localStorage.setItem('timerRunning', JSON.stringify(true));
    return true;
  };

  const timerPause = () => {
    setTimerRunning(false);
    localStorage.setItem('timerRunning', JSON.stringify(false));
  };

  const timerReset = () => {
    setTimerRemaining(0);
    setTimerStartDuration(0);
    setTimerRunning(false);
    localStorage.setItem('timerRemaining', 0);
    localStorage.setItem('timerStartDuration', 0);
    localStorage.setItem('timerRunning', JSON.stringify(false));
  };

  const timerGetElapsedAndReset = () => {
    const elapsed = timerStartDuration - timerRemaining;
    timerReset();
    return elapsed;
  };

  // Current display time based on mode
  const displayTime = mode === 'chronometer' ? chronoTime : timerRemaining;
  const isRunning = mode === 'chronometer' ? chronoRunning : timerRunning;

  return {
    // Display
    displayTime,
    isRunning,
    formatTime,

    // Chronometer
    chronoTime,
    chronoRunning,
    chronoStart,
    chronoPause,
    chronoToggle,
    chronoReset,
    chronoGetAndReset,

    // Timer
    timerRemaining,
    timerRunning,
    timerStartDuration,
    timerStart,
    timerPause,
    timerReset,
    timerGetElapsedAndReset,
    onTimerComplete,
  };
}
